import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from "../hooks/useToast";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/common/Card';
import { Button } from '../components/common/Button';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../components/common/Tabs';
import {
  PlayIcon,
  StopIcon,
  TrashIcon,
  ClockIcon,
  TimerIcon,
  ChevronLeftIcon,
  ListBulletIcon,
  PlusIcon,
  CheckIcon,
} from '@radix-ui/react-icons';
import { 
  getActivity, 
  deleteActivity, 
  getActivityExtensions, 
  getAvailableExtensions,
  addExtensionToActivity 
} from '../api/activity';
import { startTimer, stopTimer } from '../api/activity-log';
import { formatDuration } from '../utils/time';
import Layout from '../components/common/Layout';
import ConfirmModalAlert from '../components/common/ConfirmModalAlert';
import ActivityDetailsCard from '../components/activity/ActivityDetailsCard';
import ActivityLogsCard from '../components/activity/ActivityLogsCard';
import ThirtyDaysChallengeCard from '../components/activity/extensions/ThirtyDaysChallengeCard';
import ActivityFrequencyCard from '../components/activity/extensions/ActivityFrequencyCard';
import LongestLoggedTimeCard from '../components/activity/extensions/LongestLoggedTimeCard';

const EXTENSION_COMPONENTS = {
  'daily_frequency': ActivityFrequencyCard,
  'thirty_days_challenge': ThirtyDaysChallengeCard,
  'longest_logged_time': LongestLoggedTimeCard,
};

export default function SingleActivityPage() {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activity, setActivity] = useState(null);
  const [extensions, setExtensions] = useState([]);
  const [availableExtensions, setAvailableExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extensionsLoading, setExtensionsLoading] = useState(false);
  const [, setActiveTab] = useState('overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activityId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [activityData, extensionsData] = await Promise.all([
        getActivity(activityId),
        getActivityExtensions(activityId)
      ]);
      
      setActivity(activityData);
      setExtensions(extensionsData);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to fetch activity details',
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableExtensions = async () => {
    try {
      setExtensionsLoading(true);
      const data = await getAvailableExtensions(activityId);
      setAvailableExtensions(data);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to fetch available extensions',
        type: "error"
      });
    } finally {
      setExtensionsLoading(false);
    }
  };

  const handleAddExtension = async (extensionId) => {
    try {
      await addExtensionToActivity(activityId, extensionId);
      await Promise.all([fetchData(), fetchAvailableExtensions()]);
      toast({
        title: "Success",
        description: "Extension added successfully",
        type: "success"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to add extension',
        type: "error"
      });
    }
  };

  const handleTimerToggle = async () => {
    try {
      if (activity.in_progress) {
        await stopTimer(activityId);
      } else {
        await startTimer(activityId);
      }
      await fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to toggle timer',
        type: "error"
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteActivity(activityId);
      toast({
        title: "Success",
        description: "Activity deleted successfully",
        type: "success"
      });
      navigate('/activities');
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to delete activity',
        type: "error"
      });
    }
  };

  const renderExtensions = () => {
    // Sort extensions with default ones first
    const sortedExtensions = [...extensions].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return 0;
    });

    return sortedExtensions.map(extension => {
      const ExtensionComponent = EXTENSION_COMPONENTS[extension.code];
      if (ExtensionComponent) {
        return (
          <ExtensionComponent 
            key={extension.id}
            data={extension.data}
            properties={extension.properties}
            activityId={activityId}
          />
        );
      }
      return null;
    });
  };

  const renderAvailableExtensions = () => {
    if (extensionsLoading) {
      return (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    const addedExtensionIds = new Set(extensions.map(e => e.id));

    return (
      <div className="w-full">
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-[1200px]">
          {availableExtensions.map(extension => (
            <Card 
              key={extension.id}
              className="group relative overflow-hidden border border-border/50 bg-gradient-to-br from-background via-background to-muted/20 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              <div className="relative z-10">
                <CardHeader className="space-y-2 pb-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-semibold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                        {extension.name}
                      </CardTitle>
                    </div>
                  </div>
                  <CardDescription className="text-sm text-muted-foreground min-h-[3rem] line-clamp-2">
                    {extension.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-6">
                  <Button
                    onClick={() => handleAddExtension(extension.id)}
                    disabled={addedExtensionIds.has(extension.id)}
                    className={`w-full transition-all duration-300 ${
                      addedExtensionIds.has(extension.id)
                        ? 'bg-muted hover:bg-muted/90'
                        : 'bg-gradient-to-r from-primary via-primary to-secondary hover:from-primary/90 hover:via-primary/90 hover:to-secondary/90'
                    }`}
                    variant={addedExtensionIds.has(extension.id) ? "outline" : "default"}
                  >
                    {addedExtensionIds.has(extension.id) ? (
                      <>
                        <CheckIcon className="mr-2 h-4 w-4" />
                        Added to Activity
                      </>
                    ) : (
                      <>
                        <PlusIcon className="mr-2 h-4 w-4" />
                        Add Extension
                      </>
                    )}
                  </Button>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!activity) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)] space-y-4">
          <p className="text-muted-foreground">Activity not found</p>
          <Button onClick={() => navigate('/activities')}>
            <ChevronLeftIcon className="mr-2 h-4 w-4" />
            Back to Activities
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/activities')}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{activity.name}</h1>
              <p className="text-muted-foreground">
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleTimerToggle}
              variant={activity.in_progress ? "destructive" : "default"}
            >
              {activity.in_progress ? (
                <>
                  <StopIcon className="mr-2 h-4 w-4" />
                  Stop Timer
                </>
              ) : (
                <>
                  <PlayIcon className="mr-2 h-4 w-4" />
                  Start Timer
                </>
              )}
            </Button>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={activity.in_progress}
            >
              <TrashIcon className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
        </div>

        {/* Confirm Delete Modal */}
        <ConfirmModalAlert
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Delete Activity"
          message={`Are you sure you want to delete "${activity?.name}"?`}
          confirmText="Delete Activity"
          cancelText="Cancel"
          variant="destructive"
        />

        {/* Stats Overview */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                Times Logged
              </CardTitle>
              <ListBulletIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {activity.logs?.length || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Total log entries
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                Total Duration
              </CardTitle>
              <TimerIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatDuration(
                  activity.logs?.reduce((total, log) => {
                    const start = new Date(log.started_at);
                    const end = log.ended_at ? new Date(log.ended_at) : new Date();
                    const diffInMinutes = Math.floor((end - start) / (1000 * 60));
                    return total + diffInMinutes;
                  }, 0) || 0
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Total time tracked
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                Extensions
              </CardTitle>
              <ClockIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {extensions.filter(extension => !extension.isDefault).length || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Total count of extensions added
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs Section */}
        <Tabs 
          defaultValue="overview" 
          className="space-y-4" 
          onValueChange={(value) => {
            setActiveTab(value);
            if (value === 'extensions') {
              fetchAvailableExtensions();
            }
          }}
        >
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="logs">Activity Logs</TabsTrigger>
            <TabsTrigger value="extensions">Extensions</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <ActivityDetailsCard activity={activity} />
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 [&>*:first-child]:md:col-span-2">
              {renderExtensions()}
            </div>
          </TabsContent>

          <TabsContent value="logs" className="space-y-4">
            <ActivityLogsCard 
              logs={activity.logs} 
              activityId={activityId}
            />
          </TabsContent>

          <TabsContent value="extensions" className="space-y-4">
            {renderAvailableExtensions()}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
} 